import { FC, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from 'react-router-dom'
import { Avatar, Button } from "@mui/material"
import { PRIMARY_TRANSPARENT_GREEN } from "utils/customizedTheme"
import { CalendarMonth, TrendingDown, TrendingUp, WarningAmber } from "@mui/icons-material"
import { useGET_studentEvidenceQuery } from "redux/api/programsApi"
import { format } from "date-fns"
import ProgramStudentEvidence from "./ProgramStudentEvidence"
import ProgramStudentPlanning from "./ProgramStudentPlanning"
import { useGET_subjectsSelectQuery } from "redux/api/subjectsApi"
import { MAX_PER_PAGE } from "utils/constants"
import SelectField from "components/atoms/fields/SelectField"
import { Form, Formik } from "formik"


interface IProps {
  student: string,
}

const ProgramStudent: FC<IProps> = ({student}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

   
  const [tab, setTab] = useState('evidence')
  const [subject, setSubject] = useState(null)

  const { data: subjects } = useGET_subjectsSelectQuery({ page: 1, page_size: MAX_PER_PAGE })
  const {data} = useGET_studentEvidenceQuery({student_id: student, subject_id: subject?.id}, {skip: !student || !subject})

  useEffect(() => {
   if(!subject && subjects?.list?.length > 0){
    setSubject(subjects.list[0])
   } 

  }, [subjects])
 

  const studentData = data as any
  const deviation = studentData ? studentData.baseline_score - 50 : 0

  if(!studentData) return <></>

  return (
    <>
        <div style={{ display: 'flex', width: '100%' }}>
            <div style={{ borderRight: '1px solid #78b232', width: '220px' }}>
                <div style={{ background: '#c1e398', padding: '0px' }}>

                    <div style={{ width: '100', textAlign: 'center', cursor: 'pointer'  }} 
                        onClick={() => navigate(`/students/edit/${studentData.id}`)}>

                        <div style={{ paddingTop: '20px', paddingBottom: '20px'}}>
                            <Avatar
                                    sx={{
                                        bgcolor: PRIMARY_TRANSPARENT_GREEN,
                                        width: 100,
                                        height: 100,
                                        display: 'flex',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                        margin: 'auto'
                                    }}
                                    variant='circular'
                                    src={studentData.student_profile?.avatar}
                                    >
                            </Avatar>
                        </div>
                        <div style={{ background: '#78b332', padding: '5px', color: '#fff', fontSize: '20px', textAlign: 'center' }}>
                            { studentData.first_name } {studentData.last_name}    
                        </div>
                    </div>

                </div>
                <div style={{ padding: '10px', background: '#fcf6e5' }}>
                    <div className="details">
                        <div className="details-row"><div>{t('common.academic-id')}</div><div>{studentData.student_profile?.academic_id}</div></div>
                        <div className="details-row"><div>{t('common.grade-level')}</div><div>{studentData.student_profile?.grade_level}</div></div>
                        {studentData.group && <div className="details-row"><div>{t('common.group')}</div><div>{studentData.group}</div></div>}
                    </div>    

                    <div className="details-box">
                        <div className="details">
                            <div className="details-title">{t('common.rti-interventions')}</div>
                            <div className="details-row"><div>{t('common.tier')}</div><div>{studentData.tier}</div></div>
                        </div>
                    </div>

                    <div className="details-box">
                        <div className="details">
                            <div className="details-title">{t('common.academic-performance')}</div>
                            <div className="details-row"><div>{t('common.deviation')}</div>
                                <div>
                                    <div style={{ display: 'inline-flex', gap: 5 }}>
                                        <span>{deviation}%</span>
                                        {deviation > 0 
                                            ? <TrendingUp style={{ color : '#78b332' }} /> 
                                            : <TrendingDown style={{ color : '#f5635e' }} /> }
                                    </div>
                                </div>
                            </div>
                            <div className="details-row">
                                <div>{t('common.risk')}</div>
                                <div>
                                    <div style={{ display: 'inline-flex', gap: 5 }}>
                                        <span>{studentData.risk}%</span>
                                        <WarningAmber style={{ color: '#faaa20' }} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div style={{ padding: '20px', flex: 1 }}>
                <div className="info-box">
                    <div className="info-box-title">
                        <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '20px'}}>

                            <div style={{ width: '240px' }}>
                                {subject && <Formik 
                                  initialValues={{ subject: subject }} 
                                  validationSchema={null} 
                                  onSubmit={(values)=>{}}>
                                    <Form>
                                        <SelectField
                                            fieldProps={{label: t('common.subject'), name: 'subject' }}
                                            apiCall={useGET_subjectsSelectQuery}
                                            onChange={(value: { id: any }) => {
                                                setSubject(value)
                                            }}
                                            disableClearable
                                        />
                                    </Form>
                                </Formik>}
                            </div>
                            <div style={{ textAlign: 'left', flex: 1, fontSize: '20px', fontWeight: 700, display: 'flex', gap: 5, alignItems: 'center' }}>
                               <CalendarMonth /> 
                               <span>{studentData?.program?.created_at ? format(new Date(studentData.program.created_at), 'dd-MM-yyyy') : null}</span>
                               {studentData?.program?.duration_weeks ? <span>, {t('common.duration')}: &nbsp;
                                {studentData?.program?.duration_weeks} {t('common.weeks').toLowerCase()}</span> : null}
                            </div>
                            
                            <div>
                                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                    <Button
                                        variant={tab == 'evidence' ? 'contained' : 'outlined'}
                                        color={tab == 'evidence' ? 'primary' : 'black'}
                                        onClick={() => { setTab('evidence') }}
                                        >
                                        {`${t('common.evidence')}`}
                                    </Button>

                                    <Button sx={{ marginLeft: '10px' }}
                                        variant={tab == 'planning' ? 'contained' : 'outlined'}
                                        color={tab == 'planning' ? 'primary' : 'black'}
                                        onClick={() => { setTab('planning') }}
                                        >
                                        {`${t('common.planning')}`}
                                    </Button>
                                </div>
                                    
                            </div>
                        </div>
                    </div>
                </div>

                {tab == 'evidence' && subject && <ProgramStudentEvidence student={studentData} subject={subject} />}

                {tab =='planning' && subject && <ProgramStudentPlanning student={studentData} subject={subject}/>}
              
            </div>
        </div>
    </>
  )
}

export default ProgramStudent